import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  getBookingList, 
  getBookingDetail, 
  createBooking, 
  updateBooking, 
  deleteBooking,
  deleteBookingDay,
  cancelBooking,
  cancelAllBookingHours,
  confirmBooking,
  cancelBookingHour
} from '@/api/booking'
import MTime from '@/utils/time-handler'

export const useBookingStore = defineStore('booking', () => {
  // State
  const bookings = ref([])
  const currentBooking = ref(null)
  const loading = ref(false)
  const total = ref(0)
  
  // Booking confirmation data (for passing between add and confirm pages)
  const pendingBookingData = ref(null)
  
  // Filters
  const filters = ref({
    startDate: '',
    endDate: '',
    customerId: null,
    teacherName: null
  })

  // Getters
  const pendingBookings = computed(() => 
    bookings.value.filter(b => b.status === 'pending')
  )
  
  const confirmedBookings = computed(() => 
    bookings.value.filter(b => b.status === 'confirmed')
  )
  
  const completedBookings = computed(() => 
    bookings.value.filter(b => b.status === 'completed')
  )
  
  const cancelledBookings = computed(() => 
    bookings.value.filter(b => b.status === 'cancelled')
  )
  
  const todayBookings = computed(() => {
    const today = MTime.getCurrentDate()
    return bookings.value.filter(b => b.date?.startsWith(today))
  })

  // Actions
  const fetchBookings = async (filterOptions = {}) => {
    loading.value = true
    try {
      const params = {}
      
      const startDate = filterOptions.startDate || filters.value.startDate
      if (startDate) {
        params.startDate = startDate
      } else {
        // Default to today if not provided - use new server-side format
        params.startDate = MTime.formatQueryStartDate(MTime.getCurrentDate())
      }
      
      const endDate = filterOptions.endDate || filters.value.endDate
      if (endDate) {
        params.endDate = endDate
      } else {
        // Default to end of today - use new server-side format
        params.endDate = MTime.formatQueryEndDate(MTime.getCurrentDate())
      }
      
      // Optional parameters
      const customerId = filterOptions.customerId || filters.value.customerId
      if (customerId !== null && customerId !== undefined && customerId !== '') {
        params.customerId = customerId
      }
      
      const teacherName = filterOptions.teacherName || filters.value.teacherName
      if (teacherName !== null && teacherName !== undefined && teacherName !== '') {
        params.teacherName = teacherName
      }
      
      const res = await getBookingList(params)
      
      if (res.success) {
        // Handle both array and object responses per API spec
        if (Array.isArray(res.data)) {
          bookings.value = res.data
          total.value = res.data.length
        } else if (res.data && typeof res.data === 'object') {
          bookings.value = res.data.list || []
          total.value = res.data.total || 0
        } else {
          bookings.value = []
          total.value = 0
        }
        
        // Update filters
        if (Object.keys(filterOptions).length > 0) {
          filters.value = { ...filters.value, ...filterOptions }
        }
        
        return { success: true, data: bookings.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Fetch error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchBookingById = async (id) => {
    loading.value = true
    try {
      const res = await getBookingDetail(id)
      
      if (res.success) {
        currentBooking.value = res.data
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Fetch detail error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const addBooking = async (bookingData) => {
    console.log('[STORE] addBooking called with:', bookingData)
    try {
      const res = await createBooking(bookingData)
      console.log('[STORE] createBooking returned:', res)
      
      if (res.success) {
        // Add to local list
        if (res.data) {
          bookings.value.unshift(res.data)
          total.value++
        }
        
        console.log('[STORE] Returning success')
        return { success: true, data: res.data }
      }
      
      console.log('[STORE] Returning failure from API response')
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[STORE] Create error:', error)
      return { success: false, error: error.message }
    }
  }

  const modifyBooking = async (id, bookingData) => {
    try {
      const res = await updateBooking(id, bookingData)
      
      if (res.success) {
        // Update in local list
        const index = bookings.value.findIndex(b => b.id === id)
        if (index !== -1) {
          bookings.value[index] = { ...bookings.value[index], ...bookingData }
        }
        
        // Update current if matches
        if (currentBooking.value?.id === id) {
          currentBooking.value = { ...currentBooking.value, ...bookingData }
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Update error:', error)
      return { success: false, error: error.message }
    }
  }

  const cancelBookingAction = async (id, bookingDate, hour = 0) => {
    try {
      // Use new server-side date format
      const bookingDateUTC = MTime.formatBookingDate(bookingDate)
      
      const cancelData = {
        bookingId: id,
        hour: hour,
        bookingDate: bookingDateUTC,
        isAdmin: true
      }
      const res = await cancelBooking(id, cancelData)
      
      if (res.success) {
        // Update status in local list
        const index = bookings.value.findIndex(b => b.id === id)
        if (index !== -1) {
          bookings.value[index].status = 'cancelled'
        }
        
        // Update current if matches
        if (currentBooking.value?.id === id) {
          currentBooking.value.status = 'cancelled'
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Cancel error:', error)
      return { success: false, error: error.message }
    }
  }

  const cancelAllBookingHoursAction = async (id, bookingDate) => {
    try {
      // Use new server-side date format
      const bookingDateUTC = MTime.formatBookingDate(bookingDate)
      
      const cancelData = {
        bookingId: id,
        bookingDate: bookingDateUTC,
        isAdmin: true
      }
      const res = await cancelAllBookingHours(id, cancelData)
      
      if (res.success) {
        // Update status in local list
        const index = bookings.value.findIndex(b => b.id === id)
        if (index !== -1) {
          bookings.value[index].status = 'cancelled'
        }
        
        // Update current if matches
        if (currentBooking.value?.id === id) {
          currentBooking.value.status = 'cancelled'
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Cancel all hours error:', error)
      return { success: false, error: error.message }
    }
  }

  const confirmBookingAction = async (id) => {
    try {
      const res = await confirmBooking(id)
      
      if (res.success) {
        // Update status in local list
        const index = bookings.value.findIndex(b => b.id === id)
        if (index !== -1) {
          bookings.value[index].status = 'confirmed'
        }
        
        // Update current if matches
        if (currentBooking.value?.id === id) {
          currentBooking.value.status = 'confirmed'
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Confirm error:', error)
      return { success: false, error: error.message }
    }
  }

  const removeBooking = async (id) => {
    try {
      const res = await deleteBooking(id)
      
      if (res.success) {
        const index = bookings.value.findIndex(b => b.id === id)
        if (index !== -1) {
          bookings.value.splice(index, 1)
          total.value--
        }
        
        if (currentBooking.value?.id === id) {
          currentBooking.value = null
        }
        
        return { success: true }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Delete error:', error)
      return { success: false, error: error.message }
    }
  }

  const cancelBookingHourAction = async (bookingId, hour, bookingDate) => {
    try {
      // Use new server-side date format
      const bookingDateUTC = MTime.formatBookingDate(bookingDate)
      
      const res = await cancelBookingHour(bookingId, hour, bookingDateUTC)
      
      if (res.success) {
        // Refresh booking detail
        if (currentBooking.value?.id === bookingId) {
          await fetchBookingById(bookingId)
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Cancel hour error:', error)
      return { success: false, error: error.message }
    }
  }

  const removeBookingDay = async (bookingId) => {
    try {
      const res = await deleteBookingDay(bookingId)
      
      if (res.success) {
        // Refresh booking detail
        if (currentBooking.value?.id === bookingId) {
          await fetchBookingById(bookingId)
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Booking Store] Delete day error:', error)
      return { success: false, error: error.message }
    }
  }

  const clearCurrentBooking = () => {
    currentBooking.value = null
  }

  const clearPendingBookingData = () => {
    pendingBookingData.value = null
  }

  const resetFilters = () => {
    filters.value = {
      selfId: null,
      startDate: '',
      endDate: '',
      customerId: null
    }
  }

  return {
    // State
    bookings,
    currentBooking,
    loading,
    total,
    filters,
    pendingBookingData,
    
    // Getters
    pendingBookings,
    confirmedBookings,
    completedBookings,
    cancelledBookings,
    todayBookings,
    
    // Actions
    fetchBookings,
    fetchBookingById,
    addBooking,
    modifyBooking,
    cancelBookingAction,
    cancelAllBookingHoursAction,
    confirmBookingAction,
    removeBooking,
    removeBookingDay,
    cancelBookingHourAction,
    clearCurrentBooking,
    clearPendingBookingData,
    resetFilters
  }
})